import React, {useContext, useEffect, useRef, useState} from 'react'
import {FormControl, TextField} from "@mui/material";
import {useStyles} from "./ChangePasswordSection.css";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import useChangePassword from "./useChangePassword";
import AppbarActionButton from "../../../elements/buttons/AppbarActionButton";
import {Cancel, Save} from "@mui/icons-material";
import {AppbarContentContext} from "../../../routing/appbar/AppbarContentProvider";
import {FirebaseAuthContext} from "../../../firebase/FirebaseAuthProvider";
import {useIntl} from "react-intl";

const ChangePasswordSection = () => {
    const classes = useStyles();

    // States to store the values
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [newPasswordRepeat, setNewPasswordRepeat] = useState('')

    // References, which hold the value of the states (required to be accessible in the useEffect w/o dependencies)
    const currentPasswordRef = useRef(currentPassword)
    const newPasswordRef = useRef(newPassword)
    const newPasswordRepeatRef = useRef(newPasswordRepeat)

    const [saveButtonEnabled, setSaveButtonEnabled] = useState(false);

    // Hook to perform the actual password change and providing utility functions
    const {changePassword, cancel} = useChangePassword()

    // Hook to update the appbar
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    const {signedInUserData} = useContext(FirebaseAuthContext)

    // Internationalization for simple strings
    const intl = useIntl();

    useEffect(() => {
        setSaveButtonEnabled(currentPassword && newPassword && newPasswordRepeat && currentPassword !== newPassword && newPassword === newPasswordRepeat)
    }, [currentPassword, newPassword, newPasswordRepeat, currentPassword])

    /*
    Functions for controlling the state of the controlled input elements.
    The state is updated on each value change / key stroke, this allows a flexible and dynamic handling.
     */
    const handleChangeCurrentPassword = (event) => {
        setCurrentPassword(event.target.value)
        currentPasswordRef.current = event.target.value
    }
    const handleChangeNewPassword = (event) => {
        setNewPassword(event.target.value)
        newPasswordRef.current = event.target.value
    }

    const handleChangeNewPasswordRepeat = (event) => {
        setNewPasswordRepeat(event.target.value)
        newPasswordRepeatRef.current = event.target.value
    }

    const showIfTrue = (text, condition) => {
        if (condition) {
            return text
        }
        return ''
    }

    const getPasswordMatchInfo = () => {
        if (currentPassword && newPassword && newPasswordRepeat) {
            if (newPassword !== newPasswordRepeat) {
                return {
                    element: <ErrorIcon className={classes.errorIcon}/>,
                    text: intl.formatMessage({
                        id: 'app.profile.changePassword.warnings.passwordsNotEqual',
                        defaultMessage: 'Passwords are not equal.'
                    })
                }
            }
            if (newPassword === currentPassword) {
                return {
                    element: <ErrorIcon className={classes.errorIcon}/>,
                    text: intl.formatMessage({
                        id: 'app.profile.changePassword.warnings.newPasswordNotDifferent',
                        defaultMessage: 'New password must differ from current password.'
                    })
                }
            }
            return {
                element: <CheckCircleIcon className={classes.successIcon}/>,
                text: ''
            }
        }
        return {
            element: '',
            text: ''
        }
    }

    const getDescription = () => {
        return signedInUserData.firstName && signedInUserData.secondName
            ? signedInUserData.firstName + ' ' + signedInUserData.secondName
            : signedInUserData.displayName
    }

    // Setting the appbar content
    useEffect(() => {
        setCurrentAppbarContent({
            header: intl.formatMessage({
                id: 'app.profile.changePassword.appbar.header',
                defaultMessage: 'Change Password'
            }),
            description: getDescription(),
            actions: <>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.profile.changePassword.appbar.actions.save',
                        defaultMessage: 'Save'
                    })}
                    onClick={() => changePassword(
                        currentPasswordRef.current,
                        newPasswordRef.current,
                        newPasswordRepeatRef.current)}
                    icon={<Save/>}
                    disabled={!saveButtonEnabled}/>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.profile.changePassword.appbar.actions.cancel',
                        defaultMessage: 'Cancel'
                    })}
                    onClick={() => cancel()}
                    icon={<Cancel/>}/>
            </>
        })
    }, [saveButtonEnabled])

    return (
        <div className={classes.root}>
            <div>
                {/* Password */}
                <FormControl className={classes.formControl}>
                    <TextField variant="outlined" margin="normal" fullWidth required type="password"
                               value={currentPassword}
                               onChange={handleChangeCurrentPassword}
                               id="reg-user-password"
                               label={intl.formatMessage({
                                   id: 'app.profile.changePassword.form.password.label',
                                   defaultMessage: 'Current Password'
                               })}
                               helperText={showIfTrue(intl.formatMessage({
                                   id: 'app.profile.changePassword.form.password.help',
                                   defaultMessage: 'Enter current password'
                               }), !currentPassword)}
                               error={!currentPassword}/>
                </FormControl>
            </div>

            <div>
                {/* New password */}
                <FormControl className={classes.formControl}>
                    <TextField variant="outlined" margin="normal" fullWidth required type="password"
                               value={newPassword}
                               onChange={handleChangeNewPassword}
                               id="reg-user-passwordRepeat"
                               label={intl.formatMessage({
                                   id: 'app.profile.changePassword.form.newPassword.label',
                                   defaultMessage: 'New Password'
                               })}
                               helperText={showIfTrue(intl.formatMessage({
                                   id: 'app.profile.changePassword.form.newPassword.help',
                                   defaultMessage: 'Enter new password'
                               }), !newPassword)}
                               error={!newPassword} InputProps={{endAdornment: getPasswordMatchInfo().element}}/>
                </FormControl>

                {/* New password repeat */}
                <FormControl className={classes.formControl}>
                    <TextField variant="outlined" margin="normal" fullWidth required type="password"
                               value={newPasswordRepeat}
                               onChange={handleChangeNewPasswordRepeat}
                               id="reg-user-passwordRepeat"
                               label={intl.formatMessage({
                                   id: 'app.profile.changePassword.form.newPasswordRepeat.label',
                                   defaultMessage: 'Repeat password'
                               })}
                               helperText={showIfTrue(intl.formatMessage({
                                   id: 'app.profile.changePassword.form.newPasswordRepeat.help',
                                   defaultMessage: 'Repeat password'
                               }), !newPasswordRepeat)}
                               error={!newPasswordRepeat} InputProps={{endAdornment: getPasswordMatchInfo().element}}/>
                </FormControl>

            </div>
            <div className={classes.passwordInfo}>
                {getPasswordMatchInfo().text}
            </div>
        </div>
    )
}

export default ChangePasswordSection
